import React from "react";
import { ikUrl, ikSrcSet } from "../../utils/imagekit";
import YouTubeEmbed from "./YouTubeEmbed";

// Renders one content block. Content is structured JSON (never raw HTML),
// so everything goes through React as text — inherently safe from injection.
function Block({ block, lang }) {
  switch (block.type) {
    case "paragraph":
      return <p>{block.text}</p>;

    case "heading": {
      const level = Math.min(Math.max(block.level || 2, 2), 4); // never h1
      const Tag = `h${level}`;
      return <Tag>{block.text}</Tag>;
    }

    case "list": {
      const Tag = block.style === "ordered" ? "ol" : "ul";
      return (
        <Tag>
          {(block.items || []).map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </Tag>
      );
    }

    case "quran": {
      const ref = [
        block.surah && `سورة ${block.surah}`,
        block.ayahStart &&
          `الآية ${block.ayahStart}${
            block.ayahEnd && block.ayahEnd !== block.ayahStart
              ? `-${block.ayahEnd}`
              : ""
          }`,
      ]
        .filter(Boolean)
        .join(" • ");
      return (
        <figure className="quran-block">
          <p className="quran-block__arabic" dir="rtl" lang="ar">
            {block.arabic}
          </p>
          {block.translation && (
            <p className="quran-block__translation">{block.translation}</p>
          )}
          {ref && <figcaption className="quran-block__ref">{ref}</figcaption>}
        </figure>
      );
    }

    case "hadith": {
      const ref = [block.collection, block.reference, block.grade]
        .filter(Boolean)
        .join(" • ");
      return (
        <figure className="hadith-block">
          <p className="hadith-block__arabic" dir="rtl" lang="ar">
            {block.arabic}
          </p>
          {block.translation && (
            <p className="hadith-block__translation">{block.translation}</p>
          )}
          {ref && <figcaption className="hadith-block__ref">{ref}</figcaption>}
        </figure>
      );
    }

    case "image": {
      const src = block.url || block.path;
      if (!src) return null;
      return (
        <figure className="article-image">
          <img
            src={ikUrl(src, "w-800,q-80,f-auto")}
            srcSet={ikSrcSet(src, [400, 800, 1200])}
            sizes="(max-width: 768px) 100vw, 800px"
            alt={block.alt || ""}
            width={block.width}
            height={block.height}
            loading="lazy"
          />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    }

    case "video": {
      const src = block.url || block.path;
      if (!src) return null;
      return (
        <figure className="article-video">
          <video
            src={ikUrl(src, "q-80")}
            poster={block.poster ? ikUrl(block.poster, "w-800,q-80,f-auto") : undefined}
            controls
            preload="metadata"
            width={block.width}
            height={block.height}
          />
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    }

    case "youtube":
      return (
        <YouTubeEmbed
          videoId={block.videoId}
          title={block.title}
          caption={block.caption}
        />
      );

    default:
      return null;
  }
}

export default function ArticleContent({ blocks = [], lang, dir }) {
  return (
    <div className="article-content" dir={dir}>
      {blocks.map((block, i) => (
        <Block key={i} block={block} lang={lang} />
      ))}
    </div>
  );
}
